<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_LOAN_TRACKING_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            text="Export"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              CSV
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              Excel
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              label="Filter"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              label="Reset Filter"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
      <!--  -->

    </div>

    <b-table
      ref="loanTrackingTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="tableHeaders"
      primary-key="id"
      show-empty
      empty-text="No records available"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: account number -->
      <template #cell(accountNumber)="data">
        {{ data.value }}
      </template>

      <!-- Column: Account Name -->
      <template #cell(accountName)="data">
        {{ data.value }}
      </template>

      <template #cell(startDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>

      <template #cell(endDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>

      <template #cell(expectPrincipalAtMaturity)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      {
      <template #cell(principalDueToDate)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(principalPaidToDate)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(principalDueAndUnpaidToDate)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(principalNotYetDue)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <!-- <template #cell(principalDueUnpaid)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template> -->
      <template #cell(expectedInterestAtMaturity)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(interestPaidToDate)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(interestDueToDate)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(interestDueAndUnpaidToDate)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(interestNotYetDue)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(expectFeeAtMaturity)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(feeDueToDate)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(feePaidToDate)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(feeDueAndUnpaidToDate)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(feeNotYetDue)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(penaltyDueToDate)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(penaltyPaidToDate)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(penaltyDueAndUnpaidToDate)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>
      <template #cell(lastRepaymentDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </span>
      </template>
      <template #cell(lastRepaymentAmount)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      ok-title="Apply"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      title="Filters"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="loanTrackingRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.loanAccountNumber"
                label="Account Number"
                placeholder="Enter account number"
                type="text"
              />
            </b-col>
            <!-- Account Number -->

            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.accountName"
                label="Account Name"
                placeholder="Enter account name"
                type="text"
              />
            </b-col>
            <!-- Account Name -->

            <b-col md="6 mt-2">
              <SwitchButton
                v-model="filter.useCurrentFinancialDate"
                label="Use Current Financial Date"
                name="use-current-financial-date"
                :readonly="isReadonly"
                @input="setUseCurrentFinancialDate(filter.useCurrentFinancialDate)"
              />
            </b-col>

            <!-- Financial Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date"
                v-model="filter.financialDate"
                :min-date="new Date()"
                label="Financial Date"
                :config="datePickerConfig"
                rules="required"
                error-name="Financial Date"
                placeholder="Select financial date"
                :disabled="isDisabledText"
              />
            </b-col>
            <!-- Financial Date -->

            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="filter.productCode"
                :reduce="option => option.value"
                label="Product Name"
                placeholder="Select"
                :options="productOptions"
                item="label"
              />
            </b-col>
            <!-- Product -->

            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                label="Branch"
                placeholder="Select"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="account-officer"
                v-model="filter.accountOfficerStaffId"
                label="Account Officer"
                :options="accountOfficerOptions"
                :reduce="option => option.value"
                item="label"
                placeholder="Select"
              />
            </b-col>
            <!-- IPPIS/Oracle ID -->
            <b-col md="6">
              <InputField
                id="ippis-oracle-id"
                v-model="filter.employeeID"
                label="IPPIS/Oracle ID"
                placeholder="Enter IPPIS/Oracle ID"
                type="text"
              />
            </b-col>
            <!-- IPPIS/Oracle ID -->
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BForm,
  BModal, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import Ripple from 'vue-ripple-directive'
import XLSX from 'xlsx'
import { heightFade } from '@core/directives/animations'
import { mapActions, mapGetters } from 'vuex'
import { datePickerConfig } from '@core/mixins/datePicker'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { toBalance, formatAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../../forms/InputField.vue'
import SwitchButton from '../../../../components/switch/Switch.vue'
import toNumber from '../../../../../helpers/toNumber'
import { toCustomDateTime, toCustomDate } from '../../../../../helpers/formatDate'
import Button from '../../../../components/button/Button.vue'
import SelectField from '../../../../forms/SelectField.vue'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import { reportPermissions } from '../../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    BForm,
    InputField,
    BModal,
    BDropdown,
    BDropdownItem,
    TablePagination,
    TableEntries,
    SelectField,
    SwitchButton,
    DatePickerField,
    ValidationObserver,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, datePickerConfig, sweetAlert, permissionMixin],
  data() {
    return {
      isDisabledText: false,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: 'Save',
      isEditable: false,
      isDisabled: true,
      isBusy: false,
      required,
      modalTitle: 'Loan and Advances Report',
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account No' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'linkedAccount', sortable: true, label: 'Linked Account' },
        { key: 'accountOfficer', sortable: true, label: 'Account Officer' },
        { key: 'employeeID', sortable: true, label: 'IPPIS/Oracle ID' },
        { key: 'branch', sortable: true },
        { key: 'product', sortable: true },
        { key: 'tenure', sortable: true },
        { key: 'defaultingLoanInterest', sortable: true, label: 'Default Interest' },
        { key: 'startDate', sortable: true },
        { key: 'endDate', sortable: true },
        {
          key: 'expectPrincipalAtMaturity',
          sortable: true,
          label: 'Expect Principal At Maturity',
        },
        {
          key: 'principalDueToDate',
          sortable: true,
          label: 'Principal Due to Date',
        },
        {
          key: 'principalPaidToDate',
          sortable: true,
          label: 'Principal Paid to Date',
        },
        {
          key: 'principalDueAndUnpaidToDate',
          sortable: true,
          label: 'Principal Due & Unpaid To Date',
        },
        {
          key: 'principalNotYetDue',
          sortable: true,
          label: 'Principal Not Yet Due',
        },
        // { key: 'principalDueUnpaid', sortable: true, label: 'Principal Due & Unpaid' },
        {
          key: 'expectedInterestAtMaturity',
          sortable: true,
          label: 'Expected Interest At Maturity',
        },
        {
          key: 'interestDueToDate',
          sortable: true,
          label: 'Interest Due to Date',
        },
        {
          key: 'interestPaidToDate',
          sortable: true,
          label: 'Interest Paid to Date',
        },
        {
          key: 'interestDueAndUnpaidToDate',
          sortable: true,
          label: 'Interest Due & Unpaid',
        },
        {
          key: 'interestNotYetDue',
          sortable: true,
          label: 'Interest Not Yet Due',
        },
        {
          key: 'expectFeeAtMaturity',
          sortable: true,
          label: 'Expected Fee At Maturity',
        },
        { key: 'feeDueToDate', sortable: true, label: 'Fee Due to Date' },
        { key: 'feePaidToDate', sortable: true, label: 'Fee Paid to Date' },
        {
          key: 'feeDueAndUnpaidToDate',
          sortable: true,
          label: 'Fee Due & Unpaid',
        },
        { key: 'feeNotYetDue', sortable: true, label: 'Fee Not Yet Due' },
        {
          key: 'penaltyDueToDate',
          sortable: true,
          label: 'Penalty Due to Date',
        },
        {
          key: 'penaltyPaidToDate',
          sortable: true,
          label: 'Penalty Paid to Date',
        },
        {
          key: 'penaltyDueAndUnpaidToDate',
          sortable: true,
          label: 'Penalty Due & Unpaid',
        },
        {
          key: 'lastRepaymentDate',
          sortable: true,
          label: 'Last Repayment Date',
        },
        {
          key: 'lastRepaymentAmount',
          sortable: true,
          label: 'Last Repayment Amount',
        },
      ],

      filter: {
        loanAccountNumber: '',
        accountName: '',
        financialDate: null,
        productCode: '',
        branchCode: '',
        employeeID: '',
        useCurrentFinancialDate: true,
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      productOptions: [],
      branchOptions: [],
      accountOfficerOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map(item => ({
        'Account Number': item.accountNumber,
        'Account Name': item.accountName,
        'Linked Account': item.linkedAccount,
        'Account Officer': item.accountOfficer,
        'IPPIS/Oracle ID': item.employeeID,
        Branch: item.branch,
        Product: item.product,
        Tenure: item.tenure,
        'Defaulting Loan Interest': item.defaultingLoanInterest,
        'Start Date': this.formatCustomDate(item.startDate),
        'End Date': this.formatCustomDate(item.endDate),
        'Expect Principal At Maturity': item.expectPrincipalAtMaturity ? this.toAmount(item.expectPrincipalAtMaturity) : 0,
        'Principal Due To Date': item.principalDueToDate ? this.toAmount(item.principalDueToDate) : 0,
        'Principal Paid To Date': item.principalPaidToDate ? this.toAmount(item.principalPaidToDate) : 0,
        'Principal Due & Unpaid To Date': item.principalDueAndUnpaidToDate ? this.toAmount(item.principalDueAndUnpaidToDate) : 0,
        'Principal Not Yet Due': item.principalNotYetDue ? this.toAmount(item.principalNotYetDue) : 0,
        'Expected Interest At Maturity': item.expectedInterestAtMaturity ? this.toAmount(item.expectedInterestAtMaturity) : 0,
        'Interest Due To Date': item.interestDueToDate ? this.toAmount(item.interestDueToDate) : 0,
        'Interest Paid To Date': item.interestPaidToDate ? this.toAmount(item.interestPaidToDate) : 0,
        'Interest Due & Paid': item.interestDueAndUnpaidToDate ? this.toAmount(item.interestDueAndUnpaidToDate) : 0,
        'Interest Not Yet Due': item.interestNotYetDue ? this.toAmount(item.interestNotYetDue) : 0,
        'Expected Fee At Maturity': item.expectFeeAtMaturity ? this.toAmount(item.expectFeeAtMaturity) : 0,
        'Fee Due To Date': item.feeDueToDate ? this.toAmount(item.feeDueToDate) : 0,
        'Fee Due And Unpaid': item.feeDueAndUnpaidToDate ? this.toAmount(item.feeDueAndUnpaidToDate) : 0,
        'Fee Not Yet Due': item.feeNotYetDue ? this.toAmount(item.feeNotYetDue) : 0,
        'Penalty Due To Date': item.penaltyDueToDate ? this.toAmount(item.penaltyDueToDate) : 0,
        'Penalty Paid to Date': item.penaltyPaidToDate ? this.toAmount(item.penaltyPaidToDate) : 0,
        'Penalty Due And Unpaid': item.penaltyDueAndUnpaidToDate ? this.toAmount(item.penaltyDueAndUnpaidToDate) : 0,
        'Last Repayment Date': item.lastRepaymentDate ? this.formatCustomDate(item.lastRepaymentDate) : '',
        'Last Repayment Amount': item.lastRepaymentAmount ? this.toAmount(item.lastRepaymentAmount) : 0,
      }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchLoanTracking()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchLoanTracking()
    },
  },
  mounted() {
    this.fetchProduct()
    this.fetchBranches()
    this.fetchLoanTracking()
    this.fetchAccountOfficers()
  },
  methods: {
    ...mapActions({
      getLoanTrackingReport: 'reports/getLoanTrackingReport',
      getProduct: 'account/getProducts',
      getBranch: 'operations/getBranchList',
      getBranchByCode: 'operations/getBranchByCode',
      getAccountOfficers: 'account/getAccountOfficerList',
    }),

    async fetchAccountOfficers() {
      try {
        const { data, status } = await this.getAccountOfficers({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.LOAN_AND_ADVANCES_REPORT,
        })

        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: item.name, value: item.staffID }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    resetFilters() {
      this.filter = { ...null }
      this.filter.financialDate = null
      this.filter.useCurrentFinancialDate = true
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.financialDate = null
      this.filter.useCurrentFinancialDate = true
      this.fetchLoanTracking()
    },

    setUseCurrentFinancialDate(dataValue) {
      if (dataValue === false) {
        this.isDisabledText = false
      } else {
        this.isDisabledText = true
        this.filter.financialDate = null
      }
    },
    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async fetchBranches() {
      try {
        const response = await this.getBranch({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.LOAN_TRACKING_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.branchOptions = data.data
            .filter(dataItem => dataItem.status === 'Active')
            .map(item => ({
              label: item.name,
              value: item.code.toString(),
            }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchProduct() {
      try {
        const response = await this.getProduct({
          search: { type: 'Loan' },
          page: 1,
          limit: 1000,
          permission: reportPermissions.LOAN_TRACKING_REPORT,
        })
        const { data } = response
        if (data.data) {
          this.productOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async handleSubmit(bvModalEvt) {
      if (this.filter.useCurrentFinancialDate === false) {
        bvModalEvt.preventDefault()
        this.$refs.loanTrackingRules.validate().then(async success => {
          if (success) {
            this.fetchLoanTracking()
          }
        })
      } else {
        this.fetchLoanTracking()
      }
    },
    async fetchLoanTracking() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getLoanTrackingReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.isDisabled = false
        } else {
          this.reportData = []
          this.totalCount = 0
          this.isDisabled = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.getLoanTrackingReport({
          search: this.filter,
          page: 1,
          limit: 100000,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += [
            [orgName],
            ['Loans Tracking Report'],
            [`${(this.filter.financialDate !== null) ? `As at ${this.formatCustomDate(this.filter.financialDate)}` : 'Date: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],

            Object.keys(arrData[0]).join(','),
            ...arrData.map(item => Object.values(item).join(',')),
          ]
            .join('\n')
            .replace(/(^\[)|(\]$)/gm, '')

          const dataValue = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', dataValue)
          link.setAttribute('download', 'Loans-Tracking-Report.csv')
          link.click()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.getLoanTrackingReport({
          search: this.filter,
          page: 1,
          limit: 100000,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          const filename = 'Loans-Tracking-Report.xlsx'
          const headerLists = [
            Object.keys(arrData[0]),
          ]

          const Heading = [
            [orgName],
            ['Loans Tracking Report'],
            [`${(this.filter.financialDate !== null) ? `As at ${this.formatCustomDate(this.filter.financialDate)}` : 'Date: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],
            headerLists[0], // Headers lists
          ]
          const Data = arrData // All data lists
          const ws = XLSX.utils.aoa_to_sheet(Heading)
          XLSX.utils.sheet_add_json(ws, Data, {
            skipHeader: true,
            origin: -1,
          })
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'Loans Tracking Report')
          XLSX.writeFile(wb, filename)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    formatAmount: toBalance,
    toAmount: formatAmount,
    formatNumber: toNumber,
    formatCustomDateTime: toCustomDateTime,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
